import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import TitleItems from 'Src/components/TitleItems';
import { useCheck, useChecksSync } from 'Src/feature/check/useCheck';
import { TipAdjustForm, UsedTipAdjustForm } from 'Src/feature/check-list/useCheckListEditTips';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useGiftCard } from 'Src/feature/gift-card/useGiftCard';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { usePayment } from 'Src/feature/payment/usePayment';
import { useToast } from 'Src/feature/toasts/useToast';
import { log } from 'Src/utils/log';
import { uniqueIntFromDateMilli } from 'Src/utils/number';

const sectionId = 'EditTipModal';

const { Form, Field } = getTypedFormComponents<TipAdjustForm>();

interface EditTipModalProps {
  form: UsedTipAdjustForm;
  isOpen: boolean;
  onCancel: () => void;
}

const EditTipModal: FC<EditTipModalProps> = ({
  form,
  isOpen,
  onCancel,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const tipAdjustablePayment = form.state.watch();
  const employeesMap = useEmployee.query.map();
  const paymentAdjustTip = usePayment.mutate.adjust();
  const paymentAdjustOtherTip = usePayment.mutate.adjustOther();
  const checkGetOne = useCheck.query.getOne(tipAdjustablePayment?.checkId);
  const checksSync = useChecksSync();
  const adjustGiftCard = useGiftCard.mutate.adjustDebit();
  const paymentResponseDeclined = usePayment.response.declined();

  const uniqueTransactionNumber = useMemo(() => uniqueIntFromDateMilli() + +isOpen, [isOpen]);

  const validatePayment = (ctx: string) => {
    const checkId = tipAdjustablePayment?.checkId;
    if (typeof checkId !== 'number') { log.warn(ctx, '!checkId', { checkId }); return null; }

    const paymentId = tipAdjustablePayment?.id;
    if (typeof paymentId !== 'string') { log.warn(ctx, '!paymentId', { paymentId }); return null; }

    const tipAmountRaw = tipAdjustablePayment?.tipAmount;
    if (typeof tipAmountRaw !== 'string') { log.warn(ctx, '!tipAmount', { tipAmount: tipAmountRaw }); return null; }

    const tip = parseFloat(tipAmountRaw);
    if (!Number.isFinite(tip)) { log.warn(ctx, '!tip', { tip, tipAmountRaw }); return null; }

    const paymentAmount = tipAdjustablePayment?.paymentAmount ?? 0;
    return { checkId, paymentId, tip, total: paymentAmount + tip };
  };

  const onSaveCC = () => {
    const validated = validatePayment('onSaveCC');
    if (!validated) return;
    paymentAdjustTip.mutate({
      payment: { checkId: validated.checkId, paymentId: validated.paymentId, tip: validated.tip, total: validated.total },
    }, {
      onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.paymentAdjustTip` }),
      onSuccess: async (resp) => {
        if (resp.payment.paymentStatus === 'Declined') {
          const { title, message } = paymentResponseDeclined.parse(resp.payment);
          toast.show.error({ id: resp.payment.id, title, message });
          return;
        }
        const chk = await checkGetOne.refetch();
        if (chk.data) checksSync.sync([chk.data]);
        onCancel();
      },
    });
  };

  const onSaveGiftCard = () => {
    const validated = validatePayment('onSaveGiftCard');
    if (!validated) return;
    adjustGiftCard.mutate({
      adjustGiftCardRequest: {
        checkId: validated.checkId,
        paymentId: validated.paymentId,
        tip: validated.tip,
        total: validated.total,
        transactionNumber: uniqueTransactionNumber,
      },
    }, {
      onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.paymentAdjustGiftCardTip` }),
      onSuccess: async (resp) => {
        if (resp.payment.paymentStatus === 'Declined') {
          const { title, message } = paymentResponseDeclined.parse(resp.payment);
          toast.show.error({ id: resp.payment.id, title, message });
          return;
        }
        const chk = await checkGetOne.refetch();
        if (chk.data) checksSync.sync([chk.data]);
        onCancel();
      },
    });
  };

  const onSaveOther = () => {
    const validated = validatePayment('onSaveOther');
    if (!validated) return;
    paymentAdjustOtherTip.mutate({
      payment: { checkId: validated.checkId, paymentId: validated.paymentId, tip: validated.tip, total: validated.total },
    }, {
      onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.paymentAdjustOtherTip` }),
      onSuccess: async (resp) => {
        if (resp.payment.paymentStatus === 'Declined') {
          const { title, message } = paymentResponseDeclined.parse(resp.payment);
          toast.show.error({ id: resp.payment.id, title, message });
          return;
        }
        const chk = await checkGetOne.refetch();
        if (chk.data) checksSync.sync([chk.data]);
        onCancel();
      },
    });
  };

  const onSave = () => {
    if (tipAdjustablePayment.paymentType === 'CC') onSaveCC();
    else if (tipAdjustablePayment.paymentType === 'Other') onSaveOther();
    else onSaveGiftCard();
  };

  const employee = tipAdjustablePayment?.employeeId ? employeesMap.data?.get(tipAdjustablePayment?.employeeId) : undefined;

  return (
    <Modal
      data-testid={sectionId}
      open={isOpen}
      onBackDropClick={onCancel}
      loading={paymentAdjustTip.isPending || adjustGiftCard.isPending || paymentAdjustOtherTip.isPending}
    >
      <ModalHeader>
        <ModalTitle copy={t('labels.addTip')} />
      </ModalHeader>

      <ModalContent>
        <Flex fullHeight fullWidth>
          <TitleItems
            variant={['background', 'large']}
            justifyContent="space-between"
            items={[
              { primary: t('check.labels.checkNumber'), secondary: tipAdjustablePayment?.checkNumber ?? '' },
              { primary: t('labels.paymentCard'), secondary: tipAdjustablePayment?.paymentMarker ?? '' },
              { primary: t('check.labels.paymentAmount'), secondary: <Copy type="currency" value={tipAdjustablePayment?.paymentAmount ?? 0} /> },
            ]}
            footer={(
              <Copy
                variant="caption"
                value={t('checkListEditTip.footerDescription', {
                  tableName: tipAdjustablePayment?.tableName,
                  employeeName: employee ? t('labels.util.employeeFullName', employee) : undefined,
                  closedTime: tipAdjustablePayment?.closedAt ? DateTime.fromMillis(tipAdjustablePayment.closedAt)?.toLocaleString(DateTime.TIME_SIMPLE) : '',
                  checkTotal: tipAdjustablePayment?.checkTotal || 0,
                })}
              />
            )}
          />
          <Form usedForm={form}>
            <Field
              type="currencyInput"
              name="tipAmount"
              helperText={t('checkListEditTip.paymentTotalValue', {
                total: ((tipAdjustablePayment?.paymentAmount ?? 0) + parseFloat(tipAdjustablePayment?.tipAmount ?? 0)).toFixed(2)
              })}
            />
          </Form>
        </Flex>
      </ModalContent>

      <ModalActions>
        <ModalAction data-testid={`${sectionId}-cancel-btn`} color="secondary" onClick={onCancel} label={t('labels.cancel')} />
        <ModalAction data-testid={`${sectionId}-save-btn`} color="primary" onClick={() => void onSave()} label={t('labels.saveTip')} />
      </ModalActions>
    </Modal>
  );
};

export { EditTipModal, sectionId };
export type { EditTipModalProps };
